/**
 * /admin/zcd-convert — Owner Console tab (2026-07-09).
 *
 * Splits a tournament ball-by-ball file (Opta CSV / Vendor XLSX) into
 * one ZCD workbook per match. Two-pane layout:
 *   Left  — batch list + "New conversion" form
 *   Right — selected batch detail (converted files, per-file downloads,
 *           "Bundle & download" ZIP form, conversion report)
 *
 * Mirrors the standalone Zen Ingest desktop tool; both run the exact
 * same Python converters (apps/admin_tools/zcd_converters/).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Loader2, UploadCloud, FileDown, Trash2, Package,
  FileSpreadsheet, AlertTriangle, CheckCircle2, ChevronRight, Combine,
  ClipboardCheck, XCircle, ChevronDown,
} from "lucide-react";
import {
  listZcdBatches, getZcdBatch, createZcdBatch,
  deleteZcdBatch, fileDownloadUrl, downloadZcdZip, downloadZcdCombined,
  attachZcdScorecard, deleteZcdScorecard,
} from "../../api/zcdConvert.api";

const STATUS_PILL = {
  PROCESSING: "bg-blue-500/15   text-blue-300   border-blue-500/40",
  DONE:       "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  ERROR:      "bg-rose-500/15    text-rose-300    border-rose-500/40",
};

const SOURCE_PILL = {
  OPTA:   "bg-amber-500/15  text-amber-300  border-amber-500/40",
  VENDOR: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  SCRAP:  "bg-cyan-500/15    text-cyan-300    border-cyan-500/40",
};

function toSlugName(s) {
  return (s || "").replace(/[^A-Za-z0-9_-]+/g, "_");
}

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ZcdConvert() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [flash, setFlash] = useState(null);

  // Upload form
  const [source, setSource] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Bundle form (shared name field for ZIP + combined xlsx)
  const [folderName, setFolderName] = useState("");
  const [zipping, setZipping] = useState(false);
  const [combining, setCombining] = useState(false);

  // Scorecard attach form on an existing batch
  const [scorecardAttaching, setScorecardAttaching] = useState(false);
  const [scorecardError, setScorecardError] = useState(null);
  const [scorecardExpanded, setScorecardExpanded] = useState({}); // {matchKey: true}

  const flashFor = useCallback((kind, text, ms = 4000) => {
    setFlash({ kind, text });
    setTimeout(() => setFlash(null), ms);
  }, []);

  const refreshBatches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listZcdBatches();
      setBatches(data.batches || []);
    } catch (e) {
      flashFor("err", "Could not load batches.");
    } finally {
      setLoading(false);
    }
  }, [flashFor]);

  useEffect(() => { refreshBatches(); }, [refreshBatches]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const { data } = await getZcdBatch(id);
      setDetail(data);
      setFolderName(toSlugName(data.batch?.name || `batch_${id}`));
    } catch (e) {
      flashFor("err", "Could not load batch detail.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [flashFor]);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  async function onSubmitNew(e) {
    e.preventDefault();
    setUploadError(null);
    if (!source) { setUploadError("Pick a source (OPTA or VENDOR)."); return; }
    if (!file)   { setUploadError("Choose a ball-by-ball file."); return; }
    setUploading(true);
    setUploadPct(0);
    try {
      const { data } = await createZcdBatch({
        name, source, file, scorecard, onProgress: setUploadPct,
      });
      setName(""); setSource(""); setFile(null); setScorecard(null);
      setUploadPct(0);
      const vs = data.verification?.summary;
      let msg = `Converted ${data.batch?.matches || 0} match(es).`;
      if (vs) {
        msg += ` Scorecard: ${vs.matches_pass} pass / ${vs.matches_warn} warn / ${vs.matches_fail} fail.`;
      }
      flashFor("ok", msg);
      await refreshBatches();
      if (data.batch?.id) setSelectedId(data.batch.id);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed.";
      setUploadError(detail);
    } finally {
      setUploading(false);
    }
  }

  async function onDeleteBatch(id) {
    if (!confirm("Delete this batch and every generated file?")) return;
    try {
      await deleteZcdBatch(id);
      if (selectedId === id) setSelectedId(null);
      flashFor("ok", "Deleted.");
      await refreshBatches();
    } catch (e) {
      flashFor("err", "Delete failed.");
    }
  }

  async function onZipDownload(e) {
    e.preventDefault();
    if (!detail?.batch?.id) return;
    setZipping(true);
    try {
      const res = await downloadZcdZip(detail.batch.id, folderName);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${toSlugName(folderName) || "batch"}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      flashFor("err", "Zip download failed.");
    } finally {
      setZipping(false);
    }
  }

  async function onCombinedDownload() {
    if (!detail?.batch?.id) return;
    if (!folderName || !folderName.trim()) {
      flashFor("err", "Give the combined workbook a name.");
      return;
    }
    setCombining(true);
    try {
      const res = await downloadZcdCombined(detail.batch.id, folderName);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${toSlugName(folderName) || "batch"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      flashFor("err", "Combined-workbook download failed.");
    } finally {
      setCombining(false);
    }
  }

  async function onAttachScorecard(evt) {
    const chosen = evt.target.files?.[0];
    evt.target.value = ""; // let the same file be re-picked after error
    if (!chosen || !detail?.batch?.id) return;
    setScorecardError(null);
    setScorecardAttaching(true);
    try {
      const { data } = await attachZcdScorecard({
        batchId: detail.batch.id, file: chosen,
      });
      setDetail((prev) => prev ? {
        ...prev,
        batch: data.batch || prev.batch,
        scorecard: data.scorecard || null,
        verification: data.verification || null,
      } : prev);
      const vs = data.verification?.summary;
      if (vs) {
        flashFor("ok",
          `Scorecard checked: ${vs.matches_pass} pass / ${vs.matches_warn} warn / ${vs.matches_fail} fail.`);
      } else {
        flashFor("ok", "Scorecard attached.");
      }
      await refreshBatches();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Attach failed.";
      setScorecardError(msg);
    } finally {
      setScorecardAttaching(false);
    }
  }

  async function onDetachScorecard() {
    if (!detail?.batch?.id) return;
    if (!confirm("Remove the scorecard and verification report?")) return;
    try {
      await deleteZcdScorecard(detail.batch.id);
      setDetail((prev) => prev ? {
        ...prev,
        batch: {
          ...prev.batch,
          scorecard_filename: null,
          scorecard_uploaded_at: null,
          verification_summary: null,
          verification_at: null,
          verification_error: null,
        },
        scorecard: null,
        verification: null,
      } : prev);
      flashFor("ok", "Scorecard removed.");
      await refreshBatches();
    } catch (e) {
      flashFor("err", "Remove failed.");
    }
  }

  return (
    <div className="p-1">
      <header className="mb-4">
        <div className="flex items-center gap-2 justify-between">
          <div>
            <h1 className="text-xl font-semibold">ZCD Conversion</h1>
            <p className="text-[12.5px] text-text-secondary mt-0.5">
              Upload a tournament ball-by-ball file. Every match becomes its own
              ZCD workbook — download individually or bundle as a named ZIP.
            </p>
          </div>
          <button
            type="button"
            onClick={refreshBatches}
            className="flex items-center gap-1.5 text-[12.5px]
                       px-2.5 py-1.5 rounded-md border border-border-default
                       hover:bg-bg-secondary"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {flash && (
          <div className={`mt-3 px-3 py-2 rounded-md border text-[12.5px]
            ${flash.kind === "ok"
              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
              : "bg-rose-500/10 border-rose-500/40 text-rose-300"}`}>
            {flash.text}
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4 items-start">

        {/* ── Left pane ───────────────────────────────────────────── */}
        <div className="space-y-4 min-w-0">

          {/* New conversion form */}
          <section className="bg-bg-primary border border-border-default rounded-lg p-4">
            <h2 className="text-[13px] font-semibold mb-1 flex items-center gap-2">
              <UploadCloud className="w-3.5 h-3.5" /> New conversion
            </h2>
            <p className="text-[11.5px] text-text-secondary mb-3">
              Accepts .csv or .xlsx / .xls. Conversion may take a minute or two
              on tournament-sized files.
            </p>
            <form onSubmit={onSubmitNew} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-[11.5px] text-text-secondary">
                  Batch name
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. TNPL 2025"
                    className="mt-1 w-full px-2.5 py-1.5 rounded-md
                               bg-bg-secondary border border-border-default
                               text-[13px] text-text-primary
                               focus:outline-none focus:ring-1 focus:ring-button-primary"
                  />
                </label>
                <label className="block text-[11.5px] text-text-secondary">
                  Source <span className="text-rose-400">*</span>
                  <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    required
                    className="mt-1 w-full px-2.5 py-1.5 rounded-md
                               bg-bg-secondary border border-border-default
                               text-[13px] text-text-primary
                               focus:outline-none focus:ring-1 focus:ring-button-primary"
                  >
                    <option value="">— pick —</option>
                    <option value="OPTA">OPTA</option>
                    <option value="VENDOR">VENDOR</option>
                    <option value="SCRAP">SCRAP</option>
                  </select>
                </label>
              </div>
              <label className="block text-[11.5px] text-text-secondary">
                Ball-by-ball file <span className="text-rose-400">*</span>
                <input
                  type="file"
                  accept=".csv,.xls,.xlsx,.xlsm"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-[12.5px] text-text-primary
                             file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                             file:border-0 file:bg-button-primary file:text-white
                             file:cursor-pointer"
                />
              </label>

              <label className="block text-[11.5px] text-text-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <ClipboardCheck className="w-3 h-3" />
                  Scorecard workbook <span className="text-text-secondary">(optional)</span>
                </span>
                <input
                  type="file"
                  accept=".xls,.xlsx,.xlsm"
                  onChange={(e) => setScorecard(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-[12.5px] text-text-primary
                             file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                             file:border-0 file:bg-bg-secondary file:text-text-primary
                             file:border file:border-border-default
                             file:cursor-pointer"
                />
                <span className="block mt-1 text-[10.5px] text-text-secondary">
                  One sheet per match; used only to verify totals + batting +
                  bowling cards. Never blocks conversion.
                </span>
                {scorecard && (
                  <span className="block mt-0.5 text-[10.5px] text-emerald-300 font-mono truncate">
                    {scorecard.name}
                  </span>
                )}
              </label>

              {uploading && (
                <div className="rounded-md bg-bg-secondary border border-border-default p-2">
                  <div className="flex items-center gap-2 text-[12px] text-text-secondary">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Converting…"}
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-bg-primary overflow-hidden">
                    <div
                      className="h-full bg-button-primary transition-all"
                      style={{ width: `${uploadPct}%` }}
                    />
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="flex items-start gap-2 rounded-md bg-rose-500/10
                                border border-rose-500/40 px-2.5 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                  <span className="text-[12px] text-rose-300">{uploadError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={uploading}
                className="w-full py-2 rounded-md bg-button-primary
                           text-white font-medium text-[13px]
                           hover:opacity-90 disabled:opacity-50
                           disabled:cursor-not-allowed"
              >
                {uploading ? "Working…" : "Upload & convert"}
              </button>
            </form>
          </section>

          {/* Batch list */}
          <section className="bg-bg-primary border border-border-default rounded-lg overflow-hidden">
            <header className="px-4 py-3 border-b border-border-default flex items-center justify-between">
              <h2 className="text-[13px] font-semibold">Batches</h2>
              <span className="text-[11px] text-text-secondary">
                {batches.length}
              </span>
            </header>
            {loading ? (
              <div className="p-4 text-[12.5px] text-text-secondary flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : batches.length === 0 ? (
              <div className="p-4 text-[12.5px] text-text-secondary">
                No conversions yet. Upload a ball-by-ball file above.
              </div>
            ) : (
              <ul className="divide-y divide-border-default">
                {batches.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(b.id)}
                      className={`w-full text-left px-4 py-2.5 flex items-center
                                  gap-3 hover:bg-bg-secondary/60
                                  ${selectedId === b.id ? "bg-bg-secondary" : ""}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">
                          {b.name}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5
                                            rounded border text-[10px]
                                            ${SOURCE_PILL[b.source] || ""}`}>
                            {b.source}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5
                                            rounded border text-[10px]
                                            ${STATUS_PILL[b.status] || ""}`}>
                            {b.status}
                          </span>
                          <span className="text-[11px] text-text-secondary">
                            {b.matches} match{b.matches === 1 ? "" : "es"}
                          </span>
                          {b.failures ? (
                            <span className="text-[11px] text-amber-300">
                              {b.failures} warning{b.failures === 1 ? "" : "s"}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 text-[10.5px] text-text-secondary font-mono truncate">
                          {b.input_filename}
                        </div>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* ── Right pane ──────────────────────────────────────────── */}
        <div className="min-w-0">
          {!selectedId ? (
            <section className="bg-bg-primary border border-border-default rounded-lg p-8 text-center">
              <FileSpreadsheet className="w-8 h-8 text-text-secondary mx-auto mb-2" />
              <p className="text-[13px] text-text-secondary">
                Select a batch on the left to see the generated match workbooks
                and bundle them into a ZIP.
              </p>
            </section>
          ) : detailLoading || !detail ? (
            <section className="bg-bg-primary border border-border-default rounded-lg p-6">
              <div className="flex items-center gap-2 text-[12.5px] text-text-secondary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading batch…
              </div>
            </section>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <section className="bg-bg-primary border border-border-default rounded-lg p-4 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-[15px] font-semibold truncate">
                    {detail.batch.name}
                  </h2>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-1.5 py-0.5
                                      rounded border text-[10px]
                                      ${SOURCE_PILL[detail.batch.source] || ""}`}>
                      {detail.batch.source}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5
                                      rounded border text-[10px]
                                      ${STATUS_PILL[detail.batch.status] || ""}`}>
                      {detail.batch.status}
                    </span>
                    <span className="text-[11.5px] text-text-secondary">
                      {detail.batch.matches} match{detail.batch.matches === 1 ? "" : "es"}
                    </span>
                    <span className="text-[10.5px] text-text-secondary font-mono">
                      {detail.batch.input_filename}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onDeleteBatch(detail.batch.id)}
                  className="flex items-center gap-1.5 text-[12px] px-2.5 py-1
                             rounded-md border border-rose-500/40 text-rose-300
                             hover:bg-rose-500/10"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </section>

              {detail.batch.error && (
                <section className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-300 mt-0.5" />
                    <div>
                      <div className="text-[12.5px] font-medium text-rose-300">
                        Conversion error
                      </div>
                      <p className="text-[12px] text-rose-300 mt-0.5 font-mono break-all">
                        {detail.batch.error}
                      </p>
                    </div>
                  </div>
                </section>
              )}

              {/* Scorecard verification panel */}
              <ScorecardPanel
                detail={detail}
                onAttach={onAttachScorecard}
                onDetach={onDetachScorecard}
                attaching={scorecardAttaching}
                error={scorecardError}
                expanded={scorecardExpanded}
                setExpanded={setScorecardExpanded}
              />

              {detail.files && detail.files.length > 0 && (
                <>
                  {/* Bundle & download */}
                  <section className="bg-bg-primary border border-border-default rounded-lg p-4">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 mb-1">
                      <Package className="w-3.5 h-3.5" /> Bundle &amp; download
                    </h3>
                    <p className="text-[11.5px] text-text-secondary mb-3">
                      Two options — <b>ZIP</b> keeps one workbook per match
                      inside a folder; <b>Combined xlsx</b> merges every match
                      into a single workbook where each sheet gets a{" "}
                      <span className="font-mono">match_id</span> column
                      prepended so the matches remain disambiguable.
                    </p>
                    <form onSubmit={onZipDownload}
                          className="flex flex-wrap gap-2 items-end">
                      <label className="block text-[11.5px] text-text-secondary flex-1 min-w-[240px]">
                        Base name (folder inside ZIP / filename of combined xlsx)
                        <input
                          type="text"
                          value={folderName}
                          onChange={(e) => setFolderName(e.target.value)}
                          required
                          className="mt-1 w-full px-2.5 py-1.5 rounded-md
                                     bg-bg-secondary border border-border-default
                                     text-[13px] text-text-primary
                                     focus:outline-none focus:ring-1 focus:ring-button-primary"
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={zipping || combining}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                   bg-button-primary text-white font-medium text-[12.5px]
                                   hover:opacity-90 disabled:opacity-50"
                      >
                        {zipping
                          ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Zipping…</>)
                          : (<><FileDown className="w-3.5 h-3.5" /> ZIP</>)}
                      </button>
                      <button
                        type="button"
                        onClick={onCombinedDownload}
                        disabled={zipping || combining}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                   border border-border-default text-[12.5px]
                                   hover:bg-bg-secondary disabled:opacity-50"
                      >
                        {combining
                          ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Combining…</>)
                          : (<><Combine className="w-3.5 h-3.5" /> Combined xlsx</>)}
                      </button>
                    </form>
                  </section>

                  {/* Files list */}
                  <section className="bg-bg-primary border border-border-default rounded-lg overflow-hidden">
                    <header className="px-4 py-3 border-b border-border-default flex items-center justify-between">
                      <h3 className="text-[13px] font-semibold">
                        Converted files
                      </h3>
                      <span className="text-[11px] text-text-secondary">
                        {detail.files.length}
                      </span>
                    </header>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px]">
                        <thead className="text-[11px] text-text-secondary bg-bg-secondary/40">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium">#</th>
                            <th className="text-left px-4 py-2 font-medium">File</th>
                            <th className="text-left px-4 py-2 font-medium">Size</th>
                            <th className="text-right px-4 py-2 font-medium"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-default">
                          {detail.files.map((f, i) => (
                            <tr key={f.name}>
                              <td className="px-4 py-1.5 text-text-secondary">{i + 1}</td>
                              <td className="px-4 py-1.5 font-mono truncate max-w-[420px]">
                                {f.name}
                              </td>
                              <td className="px-4 py-1.5 text-text-secondary">
                                {formatBytes(f.size)}
                              </td>
                              <td className="px-4 py-1.5 text-right">
                                <a
                                  href={fileDownloadUrl(detail.batch.id, f.name)}
                                  className="inline-flex items-center gap-1
                                             text-[11.5px] text-button-primary hover:underline"
                                >
                                  <FileDown className="w-3 h-3" /> download
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {/* Report */}
                  {detail.report?.report?.length ? (
                    <section className="bg-bg-primary border border-border-default rounded-lg overflow-hidden">
                      <header className="px-4 py-3 border-b border-border-default">
                        <h3 className="text-[13px] font-semibold">Conversion report</h3>
                      </header>
                      <div className="overflow-x-auto">
                        <table className="w-full text-[12px]">
                          <thead className="text-[11px] text-text-secondary bg-bg-secondary/40">
                            <tr>
                              <th className="text-left px-4 py-2 font-medium">Match</th>
                              <th className="text-left px-4 py-2 font-medium">Balls</th>
                              <th className="text-left px-4 py-2 font-medium">Innings</th>
                              <th className="text-left px-4 py-2 font-medium">Wickets</th>
                              <th className="text-left px-4 py-2 font-medium">Status</th>
                              <th className="text-left px-4 py-2 font-medium">Notes</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border-default">
                            {detail.report.report.map((row, i) => (
                              <tr key={i}>
                                <td className="px-4 py-1.5 font-mono">
                                  {row.match_id || row.match_name}
                                </td>
                                <td className="px-4 py-1.5">{row.balls}</td>
                                <td className="px-4 py-1.5">{row.innings}</td>
                                <td className="px-4 py-1.5">{row.wickets}</td>
                                <td className="px-4 py-1.5">
                                  {row.status === "PASS" ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-300">
                                      <CheckCircle2 className="w-3 h-3" /> pass
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-amber-300">
                                      <AlertTriangle className="w-3 h-3" /> {row.status || "fail"}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-1.5 text-text-secondary">
                                  {row.errors || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Scorecard verification panel ─────────────────────────────────
 *
 * Three states:
 *   (a) No scorecard uploaded → inline "Attach" prompt
 *   (b) Scorecard uploaded, verification present → summary + per-match rows
 *   (c) Scorecard uploaded but verifier crashed → error card
 */

const STATUS_STYLES = {
  PASS: {
    icon: CheckCircle2,
    color: "text-emerald-300",
    pill: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  },
  WARN: {
    icon: AlertTriangle,
    color: "text-amber-300",
    pill: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  },
  FAIL: {
    icon: XCircle,
    color: "text-rose-300",
    pill: "bg-rose-500/15 border-rose-500/40 text-rose-300",
  },
  UNMATCHED: {
    icon: FileSpreadsheet,
    color: "text-text-secondary",
    pill: "bg-bg-secondary border-border-default text-text-secondary",
  },
};

function AttachScorecardInput({ onAttach, attaching, label = "Attach scorecard" }) {
  return (
    <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                      border border-border-default text-[11.5px] cursor-pointer
                      hover:bg-bg-secondary">
      {attaching
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : <UploadCloud className="w-3.5 h-3.5" />}
      {attaching ? "Verifying…" : label}
      <input
        type="file"
        accept=".xls,.xlsx,.xlsm"
        onChange={onAttach}
        disabled={attaching}
        className="hidden"
      />
    </label>
  );
}

function ScorecardPanel({
  detail, onAttach, onDetach, attaching, error, expanded, setExpanded,
}) {
  const scorecard = detail?.scorecard;
  const verification = detail?.verification;
  const summary = verification?.summary;
  const verificationError = detail?.batch?.verification_error;

  // State (a) — nothing attached yet
  if (!scorecard) {
    return (
      <section className="bg-bg-primary border border-border-default rounded-lg p-4">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-semibold flex items-center gap-2">
              <ClipboardCheck className="w-3.5 h-3.5" /> Scorecard verification
            </h3>
            <p className="text-[11.5px] text-text-secondary mt-0.5">
              Optional. Attach a scorecard workbook (one sheet per match) to
              cross-verify the converted totals + batting + bowling cards.
              Never blocks conversion.
            </p>
          </div>
          <AttachScorecardInput
            onAttach={onAttach}
            attaching={attaching}
            label="Attach scorecard"
          />
        </header>
        {error && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-rose-500/10
                          border border-rose-500/40 px-2.5 py-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
            <span className="text-[12px] text-rose-300">{error}</span>
          </div>
        )}
      </section>
    );
  }

  // State (c) — attached but verifier crashed
  if (verificationError) {
    return (
      <section className="bg-bg-primary border border-rose-500/40 rounded-lg p-4">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-semibold flex items-center gap-2 text-rose-300">
              <AlertTriangle className="w-3.5 h-3.5" /> Scorecard verification failed
            </h3>
            <p className="text-[11px] text-text-secondary mt-0.5 font-mono truncate">
              {scorecard.filename}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <AttachScorecardInput onAttach={onAttach} attaching={attaching}
                                  label="Replace" />
            <button
              type="button"
              onClick={onDetach}
              className="inline-flex items-center gap-1 text-[11.5px] px-2 py-1
                         rounded-md border border-rose-500/40 text-rose-300
                         hover:bg-rose-500/10"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        </header>
        <p className="mt-2 text-[12px] text-rose-300 font-mono break-all">
          {verificationError}
        </p>
        {error && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-rose-500/10
                          border border-rose-500/40 px-2.5 py-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
            <span className="text-[12px] text-rose-300">{error}</span>
          </div>
        )}
      </section>
    );
  }

  // State (b) — attached with report
  const perMatch = verification?.per_match || [];
  const unmatchedFiles = summary?.unmatched_files || [];

  return (
    <section className="bg-bg-primary border border-border-default rounded-lg overflow-hidden">
      <header className="px-4 py-3 border-b border-border-default flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-semibold flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Scorecard verification
          </h3>
          <p className="text-[11px] text-text-secondary mt-0.5 font-mono truncate">
            {scorecard.filename}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AttachScorecardInput onAttach={onAttach} attaching={attaching}
                                label="Replace" />
          <button
            type="button"
            onClick={onDetach}
            className="inline-flex items-center gap-1 text-[11.5px] px-2 py-1
                       rounded-md border border-border-default text-text-secondary
                       hover:bg-bg-secondary"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      </header>

      {summary && (
        <div className="px-4 py-3 border-b border-border-default
                        flex flex-wrap gap-2 text-[11.5px]">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border
                           border-border-default text-text-secondary">
            {summary.sheets_total} sheet{summary.sheets_total === 1 ? "" : "s"}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border
                            ${STATUS_STYLES.PASS.pill}`}>
            <CheckCircle2 className="w-3 h-3" /> {summary.matches_pass} pass
          </span>
          {summary.matches_warn > 0 && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border
                              ${STATUS_STYLES.WARN.pill}`}>
              <AlertTriangle className="w-3 h-3" /> {summary.matches_warn} warn
            </span>
          )}
          {summary.matches_fail > 0 && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border
                              ${STATUS_STYLES.FAIL.pill}`}>
              <XCircle className="w-3 h-3" /> {summary.matches_fail} fail
            </span>
          )}
          {summary.unmatched_sheets?.length ? (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border
                              ${STATUS_STYLES.UNMATCHED.pill}`}>
              <FileSpreadsheet className="w-3 h-3" />
              {summary.unmatched_sheets.length} unmatched sheet{summary.unmatched_sheets.length === 1 ? "" : "s"}
            </span>
          ) : null}
          {unmatchedFiles.length > 0 && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border
                              ${STATUS_STYLES.UNMATCHED.pill}`}>
              <FileSpreadsheet className="w-3 h-3" />
              {unmatchedFiles.length} converted file{unmatchedFiles.length === 1 ? "" : "s"} with no scorecard
            </span>
          )}
        </div>
      )}

      {verification?.errors?.length > 0 && (
        <div className="px-4 py-3 border-b border-border-default bg-amber-500/5">
          <div className="text-[11.5px] font-medium text-amber-300 mb-1">
            Scorecard parser warnings
          </div>
          <ul className="space-y-0.5">
            {verification.errors.map((er, i) => (
              <li key={i} className="text-[11.5px] text-amber-300 font-mono">
                {er.sheet}: {er.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {perMatch.length === 0 ? (
        <div className="p-4 text-[12.5px] text-text-secondary">
          Scorecard workbook contained no parseable match sheets.
        </div>
      ) : (
        <ul className="divide-y divide-border-default">
          {perMatch.map((row) => {
            const key = row.sheet;
            const isOpen = !!expanded[key];
            const sty = STATUS_STYLES[row.status] || STATUS_STYLES.UNMATCHED;
            const Icon = sty.icon;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
                  className="w-full text-left px-4 py-2 flex items-center gap-3
                             hover:bg-bg-secondary/60"
                >
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${sty.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium truncate">
                      {row.label || row.sheet}
                      <span className="ml-2 text-text-secondary font-normal">
                        {row.team1} <span className="opacity-60">vs</span> {row.team2}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-text-secondary font-mono truncate">
                      {row.matched_file
                        ? row.matched_file
                        : "(no matching converted file)"}
                    </div>
                  </div>
                  {row.diffs?.length > 0 && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border
                                      text-[10px] ${sty.pill}`}>
                      {row.diffs.length} diff{row.diffs.length === 1 ? "" : "s"}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-text-secondary
                                         transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 pt-0">
                    {row.status === "UNMATCHED" ? (
                      <p className="text-[11.5px] text-text-secondary">
                        No converted workbook could be matched to this
                        scorecard sheet (team names + match number both
                        checked).
                      </p>
                    ) : row.diffs?.length === 0 ? (
                      <p className="text-[11.5px] text-emerald-300">
                        No discrepancies found.
                      </p>
                    ) : (
                      <ul className="space-y-0.5">
                        {row.diffs.map((d, i) => (
                          <li key={i} className="text-[11.5px] text-text-secondary
                                                 flex gap-2">
                            <span className="text-text-secondary/60 font-mono shrink-0 min-w-[110px]">
                              {d.kind}
                            </span>
                            <span className="min-w-0">{d.message}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unmatchedFiles.length > 0 && (
        <div className="px-4 py-3 border-t border-border-default">
          <div className="text-[11.5px] font-medium text-text-secondary mb-1">
            Converted files without a matching scorecard sheet
          </div>
          <ul className="space-y-0.5">
            {unmatchedFiles.map((fn) => (
              <li key={fn} className="text-[11px] text-text-secondary font-mono truncate">
                {fn}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="px-4 py-2 border-t border-rose-500/40 bg-rose-500/5
                        flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
          <span className="text-[12px] text-rose-300">{error}</span>
        </div>
      )}
    </section>
  );
}
